import React, { useEffect, useMemo } from "react";
import {
  BrowserRouter as Router,
  Route,
  Switch,
  Redirect,
  useLocation,
} from "react-router-dom";
import { CssBaseline, ThemeProvider } from "@mui/material";
import { createTheme } from "@mui/material/styles";
import { Global } from "@emotion/react";
import { Capacitor } from "@capacitor/core";
import { CustomThemeProvider, useCustomTheme } from "./theme-shadcn";
import { Helmet } from "react-helmet-async";
import "@aws-amplify/ui-react/styles.css";
import DeveloperLogin from "./auth/developer-login";
import { useRecoilState } from "recoil";
import OAuthCallback from "./oauth-callback";
import { WebSocketProvider } from "./ws/websocket";
import { EventProvider } from "./ws/event-context";
import UserApp from "./views/user-app";
import { ToastProvider } from "./common/toast";
import { BackdropProvider } from "./common/backdrop";
import UserLogin from "./auth/user-login";
import UserLoginCallback from "./auth/user-login-callback";
import { platformState, safeAreaInsetsState } from "./global-state";
import UserLink from "./auth/user-link";
import DeveloperLogout from "./auth/developer-logout";
import UserLogout from "./auth/user-logout";
import DeveloperLoginCallback from "./auth/developer-login-callback";
import Doc from "./setting/doc";
import PaymentSuccess from "./views/payment-success";
import InitScope from "./menu/init-scope";
import { AuthProvider } from "./auth/auth-provider";
import DesktopAuthWait from "./auth/desktop-auth-wait";
import DesktopAuthDone from "./auth/desktop-auth-done";
import ClosePage from "./auth/close-page";
import { PromptUpdater } from "./assistant/prompt";
import { AppInterfaceProvider } from "./app/app-interface-provider";
import { cleanupChatsByDays } from "./chat/chat-history-storage";
import { AfterInitProvider } from "./views/after-init";

// Global pending promises map for native interface callbacks
if (typeof window !== "undefined") {
  window._appInterfacePendingPromises =
    window._appInterfacePendingPromises || new Map();

  // Global websocket messages map for mobile platforms
  window._wsMessages = window._wsMessages || new Map();
  window._wsEventEmitter = null; // Will be set by EventProvider

  // Initialize global callbacks for native interface responses
  window._resolvePromise = (requestId, result) => {
    const promiseData = window._appInterfacePendingPromises.get(requestId);
    if (promiseData) {
      window._appInterfacePendingPromises.delete(requestId);
      try {
        // Try to parse JSON result if it's a string
        const parsedResult =
          typeof result === "string" ? JSON.parse(result) : result;
        promiseData.resolve(parsedResult);
      } catch (e) {
        // If JSON parsing fails, return the raw result
        promiseData.resolve(result);
      }
    } else {
      console.warn(
        "Received resolve callback for unknown requestId:",
        requestId
      );
    }
  };

  window._rejectPromise = (requestId, error) => {
    const promiseData = window._appInterfacePendingPromises.get(requestId);
    if (promiseData) {
      window._appInterfacePendingPromises.delete(requestId);
      try {
        // Try to parse JSON error if it's a string
        const parsedError =
          typeof error === "string" ? JSON.parse(error) : error;
        promiseData.reject(new Error(parsedError.message || parsedError));
      } catch (e) {
        // If JSON parsing fails, create error with raw message
        promiseData.reject(new Error(error));
      }
    } else {
      console.warn(
        "Received reject callback for unknown requestId:",
        requestId
      );
    }
  };

  // Handle websocket messages from native (Android/iOS)
  window._handleWsMessage = (messageData) => {
    try {
      // Parse message if it's a string
      const message =
        typeof messageData === "string" ? JSON.parse(messageData) : messageData;

      // Check if this is a response to a pending websocket request
      if (
        message.sig &&
        message.type === "srp" &&
        window._wsMessages.has(message.sig)
      ) {
        const promiseData = window._wsMessages.get(message.sig);
        window._wsMessages.delete(message.sig);
        promiseData.resolve(message);
      } else {
        // This is an unsolicited websocket event - emit it via EventContext
        if (window._wsEventEmitter) {
          window._wsEventEmitter.emit(message.target, message);
        } else {
          console.warn(
            "[WebSocket] Event emitter not available, queuing message"
          );
          // Queue the message for later processing
          setTimeout(() => {
            if (window._wsEventEmitter) {
              window._wsEventEmitter.emit("websocket", message);
            }
          }, 100);
        }
      }
    } catch (error) {
      console.error("[WebSocket] Error handling message:", error, messageData);
    }
  };
}

const PrivateRoute = ({ component: Component, authed, ...rest }) => (
  <Route {...rest} render={(props) => <Component {...props} />} />
);

// export default function App() {
const AppContent = () => {
  const [platform, setPlatform] = useRecoilState(platformState);
  const [safeAreaInsets, setSafeAreaInsets] =
    useRecoilState(safeAreaInsetsState);
  const { actualMode } = useCustomTheme();

  // Minimal MUI theme only for icons and CircularProgress
  const muiTheme = useMemo(() => {
    return createTheme({
      palette: {
        mode: actualMode,
      },
    });
  }, [actualMode]);

  useEffect(() => {
    const p = { source: "", os: "", config: {} };
    const platform = Capacitor.getPlatform();

    if (platform === "android") {
      p.source = "app";
      p.os = "android";
      const insets = JSON.parse(window.AppInterface.getSafeAreaInsets());
      window.AppInterface.hideSplashScreen();
      console.log("insets", JSON.stringify(insets));
      setSafeAreaInsets(insets);
    } else if (platform === "ios") {
      p.source = "app";
      p.os = "ios";
      const insets = window._iosSafeAreaInsets;
      setSafeAreaInsets(insets);
      if (window.webkit?.messageHandlers?.hideSplashScreen) {
        window.webkit.messageHandlers.hideSplashScreen.postMessage({});
      }
    } else if (window.__TAURI_INTERNALS__) {
      p.source = "desktop";
    } else {
      p.source = "web";
    }
    console.log("platform", JSON.stringify(p));
    setPlatform(p);

    // Low-priority cleanup task - run after app initialization
    const scheduleCleanup = () => {
      setTimeout(async () => {
        try {
          console.log("Starting scheduled chat history cleanup...");
          const result = await cleanupChatsByDays(7);
          if (result.deletedCount > 0) {
            console.log(`Cleaned up ${result.deletedCount} old chat records`);
          }
        } catch (error) {
          console.warn("Chat history cleanup failed:", error);
        }
      }, 2000); // 2 second delay to not interfere with app startup
    };

    // Schedule cleanup with low priority
    if (typeof requestIdleCallback !== "undefined") {
      // Use requestIdleCallback when available for better performance
      requestIdleCallback(scheduleCleanup, { timeout: 10000 });
    } else {
      // Fallback for browsers that don't support requestIdleCallback
      scheduleCleanup();
    }
  }, []);

  const getSubtitle = () => {
    switch (process.env.REACT_APP_ENVIRONMENT) {
      case "staging":
        return "mHome AI - staging";
      default:
        return "mHome AI";
    }
  };

  return (
    <ThemeProvider theme={muiTheme}>
      <CssBaseline />
      <GlobalScrollbarStyles />
      <Helmet>
        <title>mHome</title>
        <meta name="description" content={getSubtitle()} />
      </Helmet>
      <Router basename="/">
        <AppRoutes platform={platform} />
      </Router>
    </ThemeProvider>
  );
};

const AppRoutes = ({ platform }) => {
  const location = useLocation();

  const shouldRenderAuthProvider = () => {
    const nonAuthPaths = [
      "/terms-of-use",
      "/privacy-policy",
      "/licenses",
      "/developer-login",
      "/developer-logout",
      "/user-login",
      "/user-logout",
      "/user-link",
      "/user-login-callback",
      "/developer-login-callback",
      "/close",
      "/oauth-callback",
    ];
    const isAuthPath = !nonAuthPaths.includes(location.pathname);
    return isAuthPath;
  };

  return (
    <ToastProvider>
      <BackdropProvider>
        <AppInterfaceProvider>
          {!platform.source && <LogoLoadingScreen />}
          {platform.source && (
            <>
              <Switch>
                <Route exact path="/terms-of-use">
                  <Doc docName={"terms_of_use"} />
                </Route>
                <Route exact path="/privacy-policy">
                  <Doc docName={"privacy_policy"} />
                </Route>
                <Route exact path="/licenses">
                  <Doc docName={"licenses"} />
                </Route>
                <Route exact path="/oauth-callback">
                  <OAuthCallback />
                </Route>
                <Route exact path="/developer-login">
                  <DeveloperLogin />
                </Route>
                <Route exact path="/developer-logout">
                  <DeveloperLogout />
                </Route>
                <Route exact path="/user-login">
                  <UserLogin />
                </Route>
                <Route exact path="/user-logout">
                  <UserLogout />
                </Route>
                <Route exact path="/user-link">
                  <UserLink />
                </Route>
                <Route exact path="/user-login-callback">
                  <UserLoginCallback />
                </Route>
                <Route exact path="/developer-login-callback">
                  <DeveloperLoginCallback />
                </Route>
                <Route exact path="/close">
                  <ClosePage />
                </Route>
              </Switch>
              {shouldRenderAuthProvider() && (
                <AuthProvider>
                  <EventProvider>
                    <WebSocketProvider>
                      <AfterInitProvider>
                        <PromptUpdater>
                          <Switch>
                            <Route exact path="/desktop-auth-wait">
                              <DesktopAuthWait />
                            </Route>
                            <Route exact path="/desktop-auth-done">
                              <DesktopAuthDone />
                            </Route>
                            <Route exact path="/init-scope">
                              <InitScope />
                            </Route>
                            <Route exact path="/payment-success">
                              <PaymentSuccess />
                            </Route>
                            <PrivateRoute path="/" component={UserApp} />
                          </Switch>
                        </PromptUpdater>
                      </AfterInitProvider>
                    </WebSocketProvider>
                  </EventProvider>
                </AuthProvider>
              )}
            </>
          )}
        </AppInterfaceProvider>
      </BackdropProvider>
    </ToastProvider>
  );
};

export default function App() {
  return (
    <CustomThemeProvider>
      <AppContent />
    </CustomThemeProvider>
  );
}

const GlobalScrollbarStyles = () => (
  <Global
    styles={{
      html: {
        overflow: "hidden",
        height: "100%",
        width: "100%",
        overflowX: "hidden", // Prevent horizontal scroll
      },
      body: {
        overflow: "hidden",
        height: "100%",
        width: "100%",
        overflowX: "hidden", // Prevent horizontal scroll
      },
      "#root": {
        overflow: "hidden",
        height: "100%",
        width: "100%",
        overflowX: "hidden", // Prevent horizontal scroll
      },
      "::-webkit-scrollbar": {
        display: "none",
        width: 0,
        height: 0,
        background: "transparent",
      },
      "::-webkit-scrollbar-track": {
        display: "none",
        background: "transparent",
      },
      "::-webkit-scrollbar-thumb": {
        display: "none",
        backgroundColor: "transparent",
      },
      scrollbarWidth: "none", // Firefox 68+
      msOverflowStyle: "none", // IE and Edge
    }}
  />
);

// Add LogoLoadingScreen component
const LogoLoadingScreen = () => (
  <div className="fixed top-0 left-0 w-screen h-screen flex items-center justify-center bg-white z-[9999]">
    <img
      src="/logo512.png"
      alt="Loading..."
      className="w-32 h-32 animate-pulse"
    />
  </div>
);
